import { BreakpointObserver } from '@angular/cdk/layout';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

/** Matches the Tailwind `lg` screen, which every rule in styles/_horizontal.scss keys off. */
const DESKTOP_QUERY = '(min-width: 1280px)';

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  public readonly isDesktop$: Observable<boolean>;

  public readonly sidenavOpen$: Observable<boolean>;
  public readonly sidenavCollapsed$: Observable<boolean>;
  public readonly sidenavCollapsedOpen$: Observable<boolean>;

  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _sidenavOpen$ = new BehaviorSubject<boolean>(false);
  private readonly _sidenavCollapsed$ = new BehaviorSubject<boolean>(false);
  private readonly _sidenavCollapsedOpen$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.isDesktop$ = this.breakpointObserver.observe(DESKTOP_QUERY).pipe(map((state) => state.matches));
    this.sidenavOpen$ = this._sidenavOpen$.asObservable();
    this.sidenavCollapsed$ = this._sidenavCollapsed$.asObservable();
    this.sidenavCollapsedOpen$ = this._sidenavCollapsedOpen$.asObservable();

    // Expand the sidenav when leaving desktop: the collapsed rail only makes sense beside the content,
    // not as an overlay.
    this.isDesktop$
      .pipe(
        filter((matches) => !matches),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.expandSidenav());
  }

  public openSidenav(): void {
    this._sidenavOpen$.next(true);
  }

  public closeSidenav(): void {
    this._sidenavOpen$.next(false);
  }

  public collapseSidenav(): void {
    this._sidenavCollapsed$.next(true);
  }

  public expandSidenav(): void {
    this._sidenavCollapsed$.next(false);
  }

  public collapseOpenSidenav(): void {
    this._sidenavCollapsedOpen$.next(true);
  }

  public collapseCloseSidenav(): void {
    this._sidenavCollapsedOpen$.next(false);
  }
}
